import React, {useEffect, useState, useCallback, useMemo, useRef} from 'react';
import {
  View,
  Text,
  StyleSheet,
  RefreshControl,
  ScrollView,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Modal,
  Dimensions,
  Platform,
  Pressable,
  StatusBar,
  Animated,
} from 'react-native';
import {useSwipeDownDismiss} from '../../hooks/useSwipeDownDismiss';
import DateTimePicker, {DateType, useDefaultStyles} from 'react-native-ui-datepicker';
import TabBar from '../../components/TabBar';
import OrdersList from '../Orders/OrdersList';
import {v2GetMyHistory, v2GetTransactionTypes, v2TransactionTypeIconUrl} from '../../services/v2/api';
import type {V2HistoryRecord, V2TransactionType, V2HistoryFilters, V2Request} from '../../types/v2';
import {useAuth} from '../../contexts/AuthContext';
import {COLORS} from '../../theme/themeTokens';
import {formatSignedAmount, signedAmountStyle} from '../../utils/transactionSign';
import {parseLocalYMD, toLocalYMD} from '../../utils/localDate';
import {useLatestRequest} from '../../hooks/useLatestRequest';
import {isConfirmedStatus} from '../../utils/transactionStatus';

// ── Helpers ──────────────────────────────────────────────────────────────────

/** "deposit" → "Deposit" */
const capitalize = (s: string): string =>
  s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

/** "14.04.2026" */
const formatDate = (dateString: string): string => {
  const d = new Date(dateString);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

/** Group records by date key (dd.MM.yyyy) preserving order, sorted desc */
const groupByDate = (
  items: V2HistoryRecord[],
): {date: string; items: V2HistoryRecord[]}[] => {
  // Primary: date DESC — самая свежая дата сверху. Secondary:
  // sort_order DESC — для pair-row'ов (debit baseSort+1, credit
  // baseSort+2) credit оказывается сверху. Без secondary'а на
  // одинаковых date Array.sort давал arbitrary order. См. зеркало
  // в OneAdmin UserHistoryPage / AllHistoryPage.
  const sorted = [...items].sort((a, b) => {
    const dateDiff = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (dateDiff !== 0) return dateDiff;
    return (b.sort_order ?? 0) - (a.sort_order ?? 0);
  });
  const map = new Map<string, V2HistoryRecord[]>();
  for (const rec of sorted) {
    const key = formatDate(rec.date);
    const group = map.get(key);
    if (group) {
      group.push(rec);
    } else {
      map.set(key, [rec]);
    }
  }
  return Array.from(map.entries()).map(([date, items]) => ({date, items}));
};

// formatAmount + sign visuals moved to src/utils/transactionSign so the
// "catalog-only" rule (no numeric fallback) is enforced by a single
// source across history lists. Import below pulls the shared helpers.

/** Check if transaction type is a withdraw (contains "withdr", case-insensitive) */
const isWithdrawType = (typeName: string): boolean => {
  return typeName.toLowerCase().includes('withdr');
};

/**
 * Collapse a transaction-type name to a stable category key.
 *
 * Backend has historically shipped several spelling/casing variants for the
 * same logical type ("Withdraw", "Withdrawal", "Withdrawel"; "Deposit",
 * "Deposits"). The transaction-types catalog endpoint and the actual history
 * rows can each pick a different variant, which breaks pill-based filtering
 * if we compare by id or raw name. Normalizing both sides to a category
 * keeps the filter resilient to that drift.
 */
const normalizeTypeName = (name: string | null | undefined): string => {
  const lower = (name || '').toLowerCase().trim();
  if (lower.includes('withdr')) {
    return 'withdraw';
  }
  // "depos" covers legacy "Deposit"/"Deposits"; "inflow" is the canonical
  // backend name after the v1_2_transaction_types rename (migration 00065).
  if (lower.includes('depos') || lower.includes('inflow')) {
    return 'deposit';
  }
  return lower;
};

// ── Filter Pill ──────────────────────────────────────────────────────────────

const FilterPill: React.FC<{
  label: string;
  active: boolean;
  onPress: () => void;
}> = ({label, active, onPress}) => (
  <TouchableOpacity
    style={[styles.filterPill, active && styles.filterPillActive]}
    onPress={onPress}
    activeOpacity={0.7}>
    <Text style={[styles.filterPillText, active && styles.filterPillTextActive]}>
      {label}
    </Text>
  </TouchableOpacity>
);

/** Separator between "All" and scrollable type pills */

// ── Transaction Item ─────────────────────────────────────────────────────────

const TransactionItem: React.FC<{
  item: V2HistoryRecord;
  typeIconUri: string | null;
  amountSign: V2TransactionType['amount_sign'] | null;
}> = ({item, typeIconUri, amountSign}) => {
  const formattedAmount = formatSignedAmount(item.amount, amountSign);
  const {color: amountColor} = signedAmountStyle(amountSign);
  // Kept for the middle-column type-name color split (legacy gradient
  // between "outflow" and "inflow" styles — same boolean source as
  // before, just derived from the catalog instead of the amount).
  const isNegative = amountSign === 'negative';
  // BC-shim: matches both legacy (`failed`/`completed`) и новый словарь
  // (`cancelled`/`confirmed`). TODO: убрать legacy после деплоя backend 1.4.
  const isCancelled =
    item.transaction_status_name.toLowerCase() === 'cancelled' ||
    item.transaction_status_name.toLowerCase() === 'failed';
  const isPending = item.transaction_status_name.toLowerCase() === 'pending';
  const isCompleted =
    item.transaction_status_name.toLowerCase() === 'completed' ||
    item.transaction_status_name.toLowerCase() === 'success' ||
    item.transaction_status_name.toLowerCase() === 'confirmed';
  const isWithdraw = isWithdrawType(item.transaction_type_name);

  return (
    <View style={styles.txRow}>
      {/* Left: Icon — admin-managed per transaction type */}
      <View style={styles.txIconContainer}>
        {typeIconUri ? (
          <Image
            source={{uri: typeIconUri}}
            style={styles.txIcon}
            resizeMode="contain"
          />
        ) : (
          <View style={[styles.txIcon, styles.txIconPlaceholder]}>
            <Text style={styles.txIconPlaceholderText}>
              {capitalize(item.transaction_type_name).charAt(0) || '?'}
            </Text>
          </View>
        )}
      </View>

      {/* Middle: type + status */}
      <View style={styles.txInfo}>
        <Text
          style={[
            styles.txTypeName,
            isNegative ? styles.txTypeNameNegative : styles.txTypeNamePositive,
          ]}
          numberOfLines={1}>
          {capitalize(item.transaction_type_name)}
        </Text>
      </View>

      {/* Right: amount + USD */}
      <View style={styles.txAmountBlock}>
        <Text
          style={[styles.txAmount, {color: amountColor}]}
          numberOfLines={1}>
          {formattedAmount} {item.token_symbol || item.currency}
        </Text>
      </View>
    </View>
  );
};

// ── Skeleton ─────────────────────────────────────────────────────────────────

const SkeletonItem: React.FC = () => (
  <View style={styles.txRow}>
    <View style={[styles.txIcon, styles.skeleton]} />
    <View style={styles.txInfo}>
      <View style={[styles.skeletonLine, {width: 80}]} />
      <View style={[styles.skeletonLine, {width: 60, height: 18, marginTop: 4}]} />
    </View>
    <View style={styles.txAmountBlock}>
      <View style={[styles.skeletonLine, {width: 70, alignSelf: 'flex-end'}]} />
      <View
        style={[
          styles.skeletonLine,
          {width: 55, alignSelf: 'flex-end', marginTop: 4},
        ]}
      />
    </View>
  </View>
);

// ── Filter Modal ─────────────────────────────────────────────────────────────

const {height: WINDOW_HEIGHT} = Dimensions.get('window');
const MODAL_HEIGHT = Math.round(WINDOW_HEIGHT * 0.65);

interface FilterModalProps {
  visible: boolean;
  currencies: string[]; // unique token_symbols from records
  currentFilters: V2HistoryFilters;
  onApply: (filters: V2HistoryFilters) => void;
  onClose: () => void;
  /** Web: the pill row's top on screen; the sheet lands on it (see openFilters). */
  webTop?: number;
}

const FilterModal: React.FC<FilterModalProps> = ({
  visible,
  currencies,
  currentFilters,
  onApply,
  onClose,
  webTop,
}) => {
  const [selectedCurrency, setSelectedCurrency] = useState<string | undefined>(
    currentFilters.token_symbol,
  );
  const [fromDate, setFromDate] = useState<DateType>(
    parseLocalYMD(currentFilters.date_from),
  );
  const [toDate, setToDate] = useState<DateType>(
    parseLocalYMD(currentFilters.date_to),
  );
  const [activePicker, setActivePicker] = useState<'from' | 'to' | null>(null);
  const [currencyOpen, setCurrencyOpen] = useState(false);
  const defaultStyles = useDefaultStyles('light');
  // Swipe-down-to-close — тот же паттерн что у остальных bottom-sheet'ов
  // приложения (deposit picker'ы, CardHistoryPeriodScreen). Handle-bar
  // рендерится над sheet'ом как визуальный affordance.
  const {translateY, panHandlers, listProps} = useSwipeDownDismiss(visible, onClose);

  // Sync state when opening with current filters
  useEffect(() => {
    if (visible) {
      setSelectedCurrency(currentFilters.token_symbol);
      setFromDate(parseLocalYMD(currentFilters.date_from));
      setToDate(parseLocalYMD(currentFilters.date_to));
      setActivePicker(null);
      setCurrencyOpen(false);
    }
  }, [visible, currentFilters]);

  const calendarStyles = {
    ...defaultStyles,
    calendarContainer: {backgroundColor: COLORS.pureLight},
    monthContainer: {backgroundColor: COLORS.pureLight},
    yearContainer: {backgroundColor: COLORS.pureLight},
    timeContainer: {backgroundColor: COLORS.pureLight},
    header: {color: COLORS.primaryNavy},
    month_label: {color: COLORS.primaryNavy},
    year_label: {color: COLORS.primaryNavy},
    day_label: {color: COLORS.primaryNavy},
    weekday_label: {color: COLORS.blueSteel},
    selected: {backgroundColor: COLORS.steelNavy},
    selected_label: {color: COLORS.white},
    today: {borderColor: COLORS.brandOrange, borderWidth: 1},
    today_label: {color: COLORS.primaryNavy},
    outside_label: {color: '#C0C0C0'},
    button_prev: {tintColor: COLORS.primaryNavy},
    button_next: {tintColor: COLORS.primaryNavy},
    button_prev_image: {tintColor: COLORS.primaryNavy},
    button_next_image: {tintColor: COLORS.primaryNavy},
  };

  const formatDateLabel = (d: DateType): string => {
    if (!d) return '—';
    const date = new Date(d as string | number);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
  };

  // The picked day as the device's own calendar day — see utils/localDate.
  const toYMD = (d: DateType): string | undefined =>
    toLocalYMD(d as Date | string | number | null | undefined);

  const handleApply = () => {
    const filters: V2HistoryFilters = {};
    if (selectedCurrency) filters.token_symbol = selectedCurrency;
    if (fromDate) filters.date_from = toYMD(fromDate);
    if (toDate) filters.date_to = toYMD(toDate);
    onApply(filters);
  };

  const handleReset = () => {
    setSelectedCurrency(undefined);
    setFromDate(undefined);
    setToDate(undefined);
    setActivePicker(null);
    setCurrencyOpen(false);
  };

  const hasActiveFilters =
    !!selectedCurrency || !!fromDate || !!toDate;

  return (
    <Modal
      transparent
      visible={visible}
      // animationType="none" т.к. slide-in теперь драйвит useSwipeDownDismiss
      // через translateY (единый паттерн с остальными bottom-sheet'ами
      // приложения). Modal animationType="slide" параллельно с этим давал
      // бы двойную анимацию и глитчил.
      animationType="none"
      statusBarTranslucent
      onRequestClose={onClose}
    >
      <View style={modalStyles.backdrop}>
        {/* The backdrop is a sibling of the sheet, not its parent: a
            Pressable above the list takes the touch on Android and the
            native scroll never sees it. */}
        <Pressable style={StyleSheet.absoluteFill} onPress={onClose} />
        <Animated.View
          style={[modalStyles.sheet, webTop !== undefined && {marginTop: webTop}, {transform: [{translateY}]}]}
          {...panHandlers}
        >
            {/* Drag-handle bar — визуальный affordance для swipe-down close */}
            <View style={modalStyles.grabber} />
            <ScrollView
              showsVerticalScrollIndicator={false}
              contentContainerStyle={modalStyles.scrollContent}
              {...listProps}>
            {/* Header */}
            <View style={modalStyles.header}>
              <Text style={modalStyles.title}>Filters</Text>
              {hasActiveFilters && (
                <TouchableOpacity onPress={handleReset} activeOpacity={0.7}>
                  <Text style={modalStyles.resetText}>Reset</Text>
                </TouchableOpacity>
              )}
            </View>

            {/* Currency dropdown */}
            <TouchableOpacity
              style={[modalStyles.dropdownTrigger, !currencyOpen && {marginBottom: 20}]}
              onPress={() => {
                setCurrencyOpen(!currencyOpen);
              }}
              activeOpacity={0.7}>
              <Text
                style={[
                  modalStyles.dropdownTriggerText,
                  selectedCurrency && modalStyles.dropdownTriggerTextSelected,
                ]}>
                {selectedCurrency || 'Currency'}
              </Text>
              <Image
                source={require('../../assets/arrow_left_20_white.png')}
                style={[modalStyles.chevronImage, {transform: [{rotate: currencyOpen ? '90deg' : '270deg'}]}]}
                resizeMode="contain"
              />
            </TouchableOpacity>
            {currencyOpen && (
              <View style={modalStyles.dropdownList}>
                {currencies.map(cur => (
                  <TouchableOpacity
                    key={cur}
                    style={[
                      modalStyles.dropdownItem,
                      selectedCurrency === cur && modalStyles.dropdownItemActive,
                    ]}
                    onPress={() => {
                      setSelectedCurrency(selectedCurrency === cur ? undefined : cur);
                      setCurrencyOpen(false);
                    }}
                    activeOpacity={0.7}>
                    <Text
                      style={[
                        modalStyles.dropdownItemText,
                        selectedCurrency === cur && modalStyles.dropdownItemTextActive,
                      ]}>
                      {cur}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            )}

            {/* Date pickers */}
            <Text style={modalStyles.sectionLabel}>Choose period</Text>
            <View style={modalStyles.dateRow}>
              <TouchableOpacity
                style={[
                  modalStyles.dateBtn,
                  activePicker === 'from' && modalStyles.dateBtnActive,
                ]}
                onPress={() =>
                  setActivePicker(activePicker === 'from' ? null : 'from')
                }
                activeOpacity={0.7}>
                <Text style={modalStyles.dateBtnLabel}>From</Text>
                <Text style={modalStyles.dateBtnValue}>
                  {formatDateLabel(fromDate)}
                </Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[
                  modalStyles.dateBtn,
                  activePicker === 'to' && modalStyles.dateBtnActive,
                ]}
                onPress={() =>
                  setActivePicker(activePicker === 'to' ? null : 'to')
                }
                activeOpacity={0.7}>
                <Text style={modalStyles.dateBtnLabel}>To</Text>
                <Text style={modalStyles.dateBtnValue}>
                  {formatDateLabel(toDate)}
                </Text>
              </TouchableOpacity>
            </View>

            {activePicker === 'from' && (
              <View style={modalStyles.calendarWrap}>
                <DateTimePicker
                  mode="single"
                  date={fromDate}
                  onChange={({date}) => {
                    setFromDate(date);
                    setActivePicker('to');
                  }}
                  maxDate={new Date().toISOString()}
                  styles={calendarStyles}
                />
              </View>
            )}
            {activePicker === 'to' && (
              <View style={modalStyles.calendarWrap}>
                <DateTimePicker
                  mode="single"
                  date={toDate}
                  onChange={({date}) => {
                    setToDate(date);
                    setActivePicker(null);
                  }}
                  maxDate={new Date().toISOString()}
                  styles={calendarStyles}
                />
              </View>
            )}

            {/* Apply button */}
            <TouchableOpacity
              style={modalStyles.applyBtn}
              onPress={handleApply}
              activeOpacity={0.7}>
              <Text style={modalStyles.applyBtnText}>Apply</Text>
            </TouchableOpacity>
            </ScrollView>
        </Animated.View>
      </View>
    </Modal>
  );
};

// ── Main Screen ──────────────────────────────────────────────────────────────

export type HistoryTab = 'transactions' | 'orders';

interface Props {
  onNavigate: (screen: string) => void;
  onRecordPress?: (record: V2HistoryRecord) => void;
  /** Tab shown; controlled by the parent so Orders survives an order detail round trip. */
  activeTab?: HistoryTab;
  onTabChange?: (tab: HistoryTab) => void;
  onRequestPress?: (request: V2Request) => void;
}

const TransactionHistoryScreen: React.FC<Props> = ({
  onNavigate,
  onRecordPress,
  activeTab = 'transactions',
  onTabChange,
  onRequestPress,
}) => {
  // Orders mounts on first visit and then stays mounted (hidden), so its
  // filter survives switching tabs.
  const [ordersMounted, setOrdersMounted] = useState(activeTab === 'orders');
  useEffect(() => {
    if (activeTab === 'orders') {
      setOrdersMounted(true);
    }
  }, [activeTab]);
  // Web: no extra 20 — the tabs sit at the height of the other screens' titles (Home, Card details, …).
  const statusBarHeight = Platform.OS === 'android' ? 28 : Platform.OS === 'web' ? 0 : 20;
  const {isAuthenticated, user} = useAuth();
  const userLogin = user?.login || '-----';
  const [showEqvTooltip, setShowEqvTooltip] = useState(false);
  const [records, setRecords] = useState<V2HistoryRecord[]>([]);
  const [transactionTypes, setTransactionTypes] = useState<V2TransactionType[]>(
    [],
  );
  const [selectedTypeId, setSelectedTypeId] = useState<string | null>(null); // null = All

  // Two catalog maps with different semantics:
  //   - signByExactName : type.name → amount_sign. MUST be exact —
  //     each type has its own sign; normalizing would let one type
  //     overwrite another's sign (transfer_deposit_to_current is
  //     positive while transfer_current_to_deposit is negative, but
  //     both share the normalized key "deposit").
  //   - iconByCategory : normalized → icon. OK to collapse since
  //     multiple types share an icon family ("Withdraw" + "Withdraw
  //     to card") and we fall back to whichever variant has the icon.
  const signByExactName = useMemo(() => {
    const m = new Map<string, V2TransactionType['amount_sign']>();
    for (const t of transactionTypes) m.set(t.name, t.amount_sign);
    return m;
  }, [transactionTypes]);
  const iconByExactName = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) m.set(t.name, v2TransactionTypeIconUrl(t));
    return m;
  }, [transactionTypes]);
  const iconByCategory = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const t of transactionTypes) {
      const key = normalizeTypeName(t.name);
      const iconUri = v2TransactionTypeIconUrl(t);
      if (!m.has(key) || (m.get(key) == null && iconUri != null)) {
        m.set(key, iconUri);
      }
    }
    return m;
  }, [transactionTypes]);

  // Filter pills: render only types the user actually has in their
  // current history. Derived from `records` (pre-type-filter so the
  // pill list stays stable when the user taps a single pill —
  // otherwise selecting "Withdraw" would collapse the row to one
  // pill until "All" is tapped again). Updates when the modal
  // filters (currency / date range) narrow the dataset.
  const activeTypeIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of records) {
      if (r.transaction_type_id) {
        set.add(r.transaction_type_id);
      }
    }
    return set;
  }, [records]);
  const visibleTransactionTypes = useMemo(
    () => transactionTypes.filter(t => activeTypeIds.has(t.id)),
    [transactionTypes, activeTypeIds],
  );

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [activeFilters, setActiveFilters] = useState<V2HistoryFilters>({});
  // Web: the sheet's fixed offset counts a 44-pt status bar, but the page's top inset differs (a home-screen
  // app's is larger, and the sheet covered the Transactions / Orders tabs). Measure where the pill row is.
  const filterBarRef = useRef<View>(null);
  const [filterSheetTop, setFilterSheetTop] = useState<number | undefined>(undefined);
  const openFilters = () => {
    if (Platform.OS === 'web' && filterBarRef.current) {
      filterBarRef.current.measureInWindow((_x, y) => {
        setFilterSheetTop(y);
        setFilterModalVisible(true);
      });
      return;
    }
    setFilterModalVisible(true);
  };

  // A filter applied while the last list is still loading sends a new
  // request; only the answer to the newest one is shown.
  const latest = useLatestRequest();

  const fetchData = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }
    const ticket = latest.begin();
    try {
      const [historyRes, typesRes] = await Promise.all([
        v2GetMyHistory(activeFilters),
        v2GetTransactionTypes(),
      ]);
      if (!latest.isLatest(ticket)) {
        return;
      }
      // History tab shows only fully-settled rows. Pending / in_process
      // / cancelled applications stay invisible. BC-aware helper
      // accepts legacy `completed` / `success` too.
      const confirmed = (historyRes.records || []).filter(r =>
        isConfirmedStatus(r.transaction_status_name),
      );
      setRecords(confirmed);
      setTransactionTypes(typesRes.transaction_types || []);
    } catch (err) {
      console.warn('TransactionHistoryScreen: fetch error', err);
    }
  }, [isAuthenticated, activeFilters, latest]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchData();
      setLoading(false);
    })();
  }, [fetchData]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
  }, [fetchData]);

  // Filtered records
  //
  // Match in two stages:
  //   1. exact transaction_type_id (cheap, handles the common case)
  //   2. fuzzy name match — collapses spelling drift between the
  //      transaction-types catalog (drives pill labels) and the type
  //      stored on a history row, e.g. catalog says "Withdrawel" while
  //      records carry "Withdraw". Without this fallback the user picks
  //      the "Withdrawel" pill and sees an empty list because the IDs
  //      across the two endpoints diverge.
  const selectedTypeName = selectedTypeId
    ? transactionTypes.find(t => t.id === selectedTypeId)?.name ?? null
    : null;
  const selectedTypeNameLower = selectedTypeName?.toLowerCase() ?? null;

  // Match strategy:
  //  1. Primary — stable transaction_type_id match (works when catalog
  //     and history rows are aligned).
  //  2. Fallback — EXACT transaction_type_name match (case-insensitive)
  //     for the rare case where catalog/history IDs drift but the name
  //     is the same.
  //
  // The previous implementation used normalizeTypeName as the fallback,
  // which collapses every "depos*"/"inflow*" name into a single
  // "deposit" bucket. That made the "Inflow" pill also pull in
  // open_deposit_account, close_deposit_account, transfer_*_deposit
  // and similar — the filter felt broken because almost every deposit-
  // related row leaked through. Exact-name match keeps each pill scoped
  // to its own type.
  const filtered = selectedTypeId
    ? records.filter(r => {
        if (r.transaction_type_id === selectedTypeId) return true;
        if (selectedTypeNameLower &&
            r.transaction_type_name.toLowerCase() === selectedTypeNameLower) {
          return true;
        }
        return false;
      })
    : records;

  const grouped = groupByDate(filtered);

  // Unique currency list from all records (for the modal picker)
  const uniqueCurrencies = Array.from(
    new Set(records.map(r => r.token_symbol || r.currency).filter(Boolean)),
  ).sort();

  const hasActiveModalFilters =
    !!activeFilters.token_symbol ||
    !!activeFilters.date_from ||
    !!activeFilters.date_to;

  const handleApplyFilters = (filters: V2HistoryFilters) => {
    setActiveFilters(filters);
    setFilterModalVisible(false);
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <View style={styles.container}>
      <StatusBar barStyle="light-content" backgroundColor={COLORS.primaryNavy} />

      {/* Header: Transactions | Orders tabs (design: History 1) */}
      <View style={[styles.header, {paddingTop: statusBarHeight}]}>
        <View style={styles.tabStrip} accessibilityRole="tablist">
          {([['transactions', 'Transactions'], ['orders', 'Orders']] as const).map(([tab, label]) => {
            const selected = activeTab === tab;
            return (
              <TouchableOpacity
                key={tab}
                style={styles.tabButton}
                onPress={() => onTabChange?.(tab)}
                activeOpacity={0.7}
                accessibilityRole="tab"
                accessibilityState={{selected}}>
                <Text style={[styles.tabLabel, selected && styles.tabLabelActive]}>{label}</Text>
                <View style={[styles.tabUnderline, selected && styles.tabUnderlineActive]} />
              </TouchableOpacity>
            );
          })}
        </View>
      </View>

      {/* Orders tab */}
      {ordersMounted && (
        <View style={[styles.contentWrapper, activeTab !== 'orders' && styles.hidden]}>
          <OrdersList onRequestPress={request => onRequestPress?.(request)} />
        </View>
      )}

      {/* Transactions tab — content with rounded top corners */}
      <View style={[styles.contentWrapper, activeTab !== 'transactions' && styles.hidden]}>

      {/* Filter bar */}
      <View ref={filterBarRef} style={styles.filterBar}>
        <TouchableOpacity
          style={[styles.filterPill, selectedTypeId === null && styles.filterPillActive]}
          onPress={() => setSelectedTypeId(null)}
          activeOpacity={0.7}>
          <Text style={[styles.filterPillText, selectedTypeId === null && styles.filterPillTextActive]}>
            All
          </Text>
        </TouchableOpacity>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{flex: 1}}
          contentContainerStyle={{paddingRight: 16}}>
          {visibleTransactionTypes.map((t, index) => (
            <TouchableOpacity
              key={t.id}
              style={[
                styles.filterPillNoBg,
                selectedTypeId === t.id && styles.filterPillActive,
                index > 0 && {marginLeft: 4},
              ]}
              onPress={() => setSelectedTypeId(t.id)}
              activeOpacity={0.7}>
              <Text style={[styles.filterPillText, selectedTypeId === t.id && styles.filterPillTextActive]}>
                {capitalize(t.name)}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>

      {/* Filter icon button */}
      <TouchableOpacity
        style={styles.filterIconRow}
        onPress={openFilters}
        activeOpacity={0.7}>
        <Image
          source={require('../../assets/filter.png')}
          style={[
            styles.filterIcon,
            hasActiveModalFilters && {tintColor: COLORS.brandOrange},
          ]}
          resizeMode="contain"
        />
        <Text style={styles.filterIconRowLabel}>Filters</Text>
      </TouchableOpacity>

      {/* Content */}
      {loading ? (
        <ScrollView style={styles.list} contentContainerStyle={styles.listContent}>
          {[1, 2, 3, 4, 5].map(i => (
            <SkeletonItem key={i} />
          ))}
        </ScrollView>
      ) : (
        <ScrollView
          style={styles.list}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }>
          {grouped.length === 0 ? (
            <View style={styles.emptyContainer}>
              <Text style={styles.emptyText}>No transactions yet</Text>
            </View>
          ) : (
            grouped.map(group => (
              <View key={group.date}>
                <Text style={styles.dateHeader}>{group.date}</Text>
                {group.items.map(item => (
                  <TouchableOpacity
                    key={item.id}
                    activeOpacity={0.7}
                    onPress={() => onRecordPress?.(item)}>
                    {(() => {
                      const name = item.transaction_type_name;
                      const amountSign = signByExactName.get(name) ?? null;
                      const iconUri =
                        iconByExactName.get(name) ??
                        iconByCategory.get(normalizeTypeName(name)) ??
                        null;
                      return (
                        <TransactionItem
                          item={item}
                          typeIconUri={iconUri}
                          amountSign={amountSign}
                        />
                      );
                    })()}
                  </TouchableOpacity>
                ))}
              </View>
            ))
          )}
        </ScrollView>
      )}

      </View>{/* contentWrapper */}

      {/* Tab Bar */}
      <TabBar activeTab="history" onNavigate={onNavigate} />

      {/* Filter Modal */}
      <FilterModal
        visible={filterModalVisible}
        currencies={uniqueCurrencies}
        currentFilters={activeFilters}
        onApply={handleApplyFilters}
        onClose={() => setFilterModalVisible(false)}
        webTop={filterSheetTop}
      />
    </View>
  );
};

// ── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  header: {
    backgroundColor: COLORS.primaryNavy,
  },
  // Same tab look as OperationsScreen: grey hairline under each tab, orange
  // under the selected one.
  tabStrip: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    height: 44,
  },
  tabButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  tabLabel: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    color: COLORS.blueSteel,
    paddingBottom: 8,
  },
  tabLabelActive: {
    color: COLORS.white,
    fontWeight: '500',
  },
  tabUnderline: {
    height: 1,
    width: '100%',
    backgroundColor: COLORS.blueSteel,
  },
  tabUnderlineActive: {
    height: 2,
    backgroundColor: COLORS.brandOrange,
  },
  hidden: {
    display: 'none',
  },
  container: {
    flex: 1,
    // Dark backdrop so the 10px gap between the tab header and the light
    // contentWrapper reveals navy — that's how the rounded top-corners on
    // the light area become visible (same pattern as Products / Support).
    backgroundColor: COLORS.primaryNavy,
  },
  contentWrapper: {
    flex: 1,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    marginTop: 10,
    overflow: 'hidden',
  },
  list: {
    flex: 1,
  },
  headerBackground: {
    backgroundColor: COLORS.primaryNavy,
    paddingBottom: 20,
  },
  supportHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
  },
  headerIconBtn: {
    width: 40,
    height: 40,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerIcon: {
    width: 28,
    height: 28,
    tintColor: COLORS.white,
  },
  headerLogo: {
    width: 100,
    height: 28,
  },
  headerRightGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogin: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.white,
    textAlign: 'center',
  },
  headerTitleRow: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitleCol: {
    alignItems: 'center',
  },
  headerTitleInner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  balanceLabelIcon: {
    width: 13,
    height: 13,
    marginLeft: 2,
    marginBottom: 11,
  },
  eqvTooltipText: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 13,
    fontFamily: 'Inter',
  },
  filterBar: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 12,
    paddingBottom: 8,
    paddingLeft: 16,
    backgroundColor: COLORS.pureLight,
  },
  filterIconRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 12,
    margin: 16,
    backgroundColor: COLORS.pureLight,
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
    borderRadius: 8,
    gap: 8,
  },
  filterIconRowLabel: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.primaryNavy,
  },
  filterDivider: {
    width: 1,
    height: 24,
    backgroundColor: COLORS.softGrey,
    marginHorizontal: 8,
  },
  filterPill: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: COLORS.pureLight,
    alignItems: 'center',
  },
  filterPillNoBg: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: COLORS.pureLight,
    alignItems: 'center',
  },
  filterPillActive: {
    backgroundColor: COLORS.steelNavy,
  },
  filterPillText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },
  filterPillTextActive: {
    fontFamily: 'Inter',
    color: COLORS.white,
  },
  filterIcon: {
    width: 20,
    height: 20,
    tintColor: COLORS.slate,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 120,
  },
  dateHeader: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.blueSteel,
    marginTop: 16,
    marginBottom: 8,
  },
  txRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.pureLight,
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
  },
  txIconContainer: {
    marginRight: 12,
  },
  txIcon: {
    width: 36,
    height: 36,
  },
  txIconPlaceholder: {
    backgroundColor: COLORS.softGrey,
    justifyContent: 'center',
    alignItems: 'center',
  },
  txIconPlaceholderText: {
    fontSize: 18,
    fontWeight: '600',
    color: COLORS.slate,
  },
  txInfo: {
    flex: 1,
    justifyContent: 'center',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusIcon: {
    width: 14,
    height: 14,
  },
  // Unified type-label spec across history rows (see also
  // BalanceDetail / DepositAccount). primaryNavy + Inter 16/400/20.
  // Positive/negative variants kept around for backwards-compat in
  // JSX conditional usage but resolve to the same look.
  txTypeName: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
    marginBottom: 4,
  },
  txTypeNamePositive: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
  },
  txTypeNameNegative: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
    color: COLORS.primaryNavy,
  },
  statusBadgeText: {
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 16,
    fontWeight: '500',
    color: COLORS.blueSteel,
  },
  statusBadgeTextCancelled: {
    fontFamily: 'Inter',
    fontSize: 14,
    lineHeight: 16,
    fontWeight: '400',
    color: '#E53935',
  },
  txAmountBlock: {
    alignItems: 'flex-end',
    marginLeft: 8,
  },
  txAmount: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    fontStyle: 'normal',
    lineHeight: 20,
  },
  txAmountPositive: {
    color: COLORS.mint,
  },
  txAmountNegative: {
    color: COLORS.primaryNavy,
  },
  txUsd: {
    fontSize: 14,
    color: COLORS.blueSteel,
    marginTop: 2,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 100,
  },
  emptyText: {
    fontSize: 17,
    color: COLORS.blueSteel,
  },
  skeleton: {
    backgroundColor: COLORS.softGrey,
  },
  skeletonLine: {
    height: 14,
    backgroundColor: COLORS.softGrey,
    borderRadius: 4,
  },
});

// ── Modal Styles ─────────────────────────────────────────────────────────────

const modalStyles = StyleSheet.create({
  /* Matches WithdrawScreen's modal feel — transparent overlay so the
     slide-in carries the whole sheet without a dark backdrop, and the
     sheet stretches to (almost) full screen with rounded top corners. */
  // Absolutely positioned, not flex: 1 — on Android the Modal's own
  // container does not hand a definite height down, so a flexed overlay
  // sizes itself to its content: the sheet's ScrollView grew as tall as
  // the list, swallowed the sheet's bottom padding, ran off the screen
  // and had nothing left to scroll. Pinning all four edges gives the
  // chain a real height and the list scrolls again.
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
    backgroundColor: 'transparent',
  },
  sheet: {
    flex: 1,
    // Lands flush with the top of the "All / Inflow / Withdraw …" pill
    // row. Computed as:
    //   safe-area status bar (~44 iOS notched / 28 Android)
    //   + tab header (44)
    //   + contentWrapper.marginTop (10)
    //   + filterBar.paddingTop (12)
    //   ≈ +62
    // → iOS 106, Android 90 dp from device top.
    marginTop: (Platform.OS === 'android' ? 28 : 44) + 94,
    backgroundColor: COLORS.pureLight,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: 16,
  },
  // Drag-handle bar над содержимым sheet'а — визуальный signifier для
  // swipe-down close. Тот же размер что и modalHandle в остальных
  // deposit-picker'ах (WithdrawScreen, OutflowDepositScreen и т.д.)
  // для консистентности.
  grabber: {
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: COLORS.softGrey,
    alignSelf: 'center',
    marginBottom: 12,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  title: {
    fontFamily: 'Inter',
    fontSize: 22,
    fontWeight: '500',
    lineHeight: 26,
    color: COLORS.primaryNavy,
  },
  resetText: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  sectionLabel: {
    fontFamily: 'Inter',
    fontSize: 15,
    fontWeight: '600',
    color: COLORS.primaryNavy,
    marginTop: 8,
    marginBottom: 8,
  },
  dropdownTrigger: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: 44,
    paddingHorizontal: 12,
    borderRadius: 10,
    backgroundColor: COLORS.white,
    borderWidth: 1,
    borderColor: COLORS.softGrey,
    marginBottom: 1,
  },
  dropdownTriggerText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },
  dropdownTriggerTextSelected: {
    fontFamily: 'Inter',
    color: COLORS.primaryNavy,
  },
  chevronImage: {
    width: 20,
    height: 20,
    tintColor: COLORS.primaryNavy,
    marginLeft: 8,
  },
  dropdownList: {
    backgroundColor: COLORS.white,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: COLORS.softGrey,
    overflow: 'hidden',
    marginTop: 1,
    marginBottom: 20,
  },
  dropdownItem: {
    paddingVertical: 8,
    paddingHorizontal: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: COLORS.softGrey,
  },
  dropdownItemActive: {
    backgroundColor: COLORS.steelNavy,
  },
  dropdownItemText: {
    fontFamily: 'Inter',
    fontSize: 16,
    lineHeight: 22,
    fontWeight: '400',
    color: COLORS.primaryNavy,
  },
  dropdownItemTextActive: {
    fontFamily: 'Inter',
    color: COLORS.white,
  },
  dateRow: {
    flexDirection: 'row',
    marginBottom: 8,
  },
  dateBtn: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: 8,
    backgroundColor: COLORS.white,
    marginRight: 8,
    borderWidth: 1,
    borderColor: COLORS.primaryNavy,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  dateBtnActive: {
    borderColor: COLORS.primaryNavy,
  },
  dateBtnLabel: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 22,
    color: COLORS.primaryNavy,
    marginRight: 8,
  },
  dateBtnValue: {
    fontFamily: 'Inter',
    fontSize: 16,
    fontWeight: '500',
    color: COLORS.primaryNavy,
  },
  calendarWrap: {
    marginBottom: 8,
  },
  applyBtn: {
    marginTop: 16,
    backgroundColor: '#C94021',
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: 'center',
  },
  applyBtnText: {
    lineHeight: 22,
    fontFamily: 'Inter',
    color: COLORS.white,
    fontSize: 16,
    fontWeight: '500',
  },
});

export default TransactionHistoryScreen;
